








































































































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxValueAxis, DxAdaptiveLayout,
    DxLabel, DxGrid, DxArgumentAxis, DxTick, DxCommonAxisSettings, DxFont, DxPoint, DxBorder,
    DxMinorTick, DxFormat, DxVisualRange, DxTooltip, DxHoverStyle,
} from 'devextreme-vue/chart';

import { localize } from '@/core/filters';
import { ClientDate } from '@/core/helpers';
import { IMtrChartRecord } from '@/dto/operationalPlanSummary';
import {
    IExpandedState, LocalizationKeys, Formatter, IMeasureFormattingOptions, IMtrChartData,
} from '../classes';
import { OperationalDayName } from '../classes/OperationalDayName';

interface IChartData {
    date: Date;
    consumptionUnloads: number;
    deliveryUnloads: number;
}

@Component({
    components: {
        DxChart,
        DxSeries,
        DxLegend,
        DxSize,
        DxCommonSeriesSettings,
        DxValueAxis,
        DxAdaptiveLayout,
        DxLabel,
        DxGrid,
        DxArgumentAxis,
        DxTick,
        DxCommonAxisSettings,
        DxFont,
        DxPoint,
        DxBorder,
        DxMinorTick,
        DxFormat,
        DxVisualRange,
        DxTooltip,
        DxHoverStyle,
    }
})
export default class IncomeMtrChart
    extends Vue {

    private static readonly EXPANDED_HEIGHT: number = 233;
    private static readonly COLLAPSED_HEIGHT: number = 136;

    @Prop({ required: true, })
    public mtrData?: IMtrChartData & IExpandedState;

    public LocalizationKeys = LocalizationKeys;
    private nativeUnit: string | null = null;

    private readonly measureFormattingOptions: IMeasureFormattingOptions = {
        separator: ' ',
        numberFormattingOptions: {
            useGrouping: true,
            maximumFractionDigits: 2,
        },
        measures: [{
            power: 9,
            measure: localize(LocalizationKeys.MeasureMtShort),
        }, {
            power: 6,
            measure: localize(LocalizationKeys.MeasureKtShort),
        }, {
            power: 3,
            measure: localize(LocalizationKeys.MeasureTnShort),
        }, {
            power: 0,
            measure: localize(LocalizationKeys.MeasureKgShort),
        }],
    };

    public onResize(e: any) {
        const chartIncomRef = 's-mtr-chart-income';
        (this.$refs[chartIncomRef] as any)
            ?.instance
            ?.refresh();
    }

    private mounted(): void {
        if (!this.mtrData) { throw new Error('mtrData is not set'); }

        window.addEventListener('homeResize', this.onResize);
    }

    private get diagramHeight(): number {
        return this.mtrData!.isExpanded
            ? IncomeMtrChart.EXPANDED_HEIGHT
            : IncomeMtrChart.COLLAPSED_HEIGHT;
    }

    private get values(): IChartData[] {
        const result: IChartData[] = this.mtrData!.records
            .map(x => {
                const date = x.operDayName.toDate();
                date.setDate(date.getDate() + 1);

                let consumptionUnloadsVariable = x.weightByConsumptions;
                let deliveryUnloadsVariable = x.weightByRelocations;

                if (x.unitByConsumptions && x.unitByRelocations && (x.unitByConsumptions === x.unitByRelocations)) {
                    consumptionUnloadsVariable = x.countByConsumptions;
                    deliveryUnloadsVariable = x.countByRelocations;
                    this.nativeUnit = x.unitByConsumptions;
                }

                return {
                    date,
                    consumptionUnloads: consumptionUnloadsVariable,
                    deliveryUnloads: deliveryUnloadsVariable,
                };
            });

        const minDate = this.minDate;
        if (result.length === 0) {
            if (minDate) {
                result.push({
                    date: minDate,
                    consumptionUnloads: NaN,
                    deliveryUnloads: NaN,
                });
            }
        } else {
            if (minDate) {
                result.splice(0, 0, {
                    date: minDate,
                    consumptionUnloads: 0,
                    deliveryUnloads: 0,
                });
            }
        }

        return result;
    }
    private get minDate(): Date | null {
        const records = this.mtrData!.records;
        const firstData = records.length > 0
            ? records[0]
            : null;

        const result = firstData
            ? firstData.operDayName.toDate()
            : null;

        return result;
    }
    private get maxDate(): Date | null {
        const records = this.mtrData!.records;
        const lastData = records.length > 0
            ? records[records.length - 1]
            : null;

        const result = lastData
            ? lastData.operDayName.toDate()
            : null;

        return result;
    }

    private customizeTooltip(pointInfo: {
        originalValue: number,
        originalArgument: Date,
    }): { html: string } {

        const strDate = Formatter.dateToString(pointInfo.originalArgument, {
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const strValue = this.formatValue(pointInfo.originalValue);
        const html = `<div class="s-tooltip"><span class="date">${strDate}</span><br/><span class="value">${strValue}</span></div>`;

        return { html };
    }
    private formatValue(value: number): string {
        let result = '';
        if (!this.nativeUnit || this.nativeUnit === '') {
            result = Formatter.numberToMeasureString(value, this.measureFormattingOptions);
        } else {
            result = value + ' ' + this.nativeUnit!;
        }
        return result;
    }
}
